// Pricing plans rendered by <V2PricingCards/> on both the homepage and the
// dedicated /pricing page. Keep features synced with the marketing copy.
export const v2PricingPlans = [
  {
    name: 'Starter',
    description: 'Bring your own API key. Deploy your first staff member in minutes.',
    price: '$19.99',
    period: '/month',
    note: 'No contracts · cancel anytime',
    cta: 'Get started',
    href: '/register?plan=starter',
    buyKey: 'starter',
    featured: false,
    features: [
      '1 AI staff member with OpenClaw deployment',
      '8GB RAM, 25 GB storage, shared VM',
      '$40/month of included AI credits',
      'Telegram Bot Integration',
      'Control UI Dashboard',
      'Get access to top tier frontier, open-source models',
      'Web Browsing & Cron Jobs',
    ],
  },
  {
    name: 'Pro',
    description: 'Everything pre-wired so you can run multiple staff members across the team.',
    price: '$49.99',
    period: '/month',
    note: 'No contracts · cancel anytime',
    cta: 'Start with Pro',
    href: '/register?plan=pro',
    buyKey: 'pro',
    featured: true,
    features: [
      'Everything in Starter, plus:',
      'Dedicated VM with 16 GB RAM and 90 GB storage',
      '$100/month of included AI credits',
      'Brave Search API (5,000/mo) - $25 value',
      'Voice support included - Whisper STT + ElevenLabs TTS',
      'Everything pre-setup, paste your telegram token, pair bot & start',
      'Template Library (Lead Scout & more)',
      'Agent Onboarding Wizard · Usage Dashboard · Priority Support',
    ],
  },
  {
    name: 'Enterprise',
    description: 'Custom plan for teams with volume, security, or compliance requirements.',
    price: 'Custom',
    period: '',
    note: 'Volume discounts · Invoice billing · SSO',
    cta: 'Contact us',
    href: '/contact',
    featured: false,
    features: [
      'Everything in Pro, plus:',
      'Volume pricing for larger deployment fleets',
      'Invoice billing and procurement support',
      'SSO and team access controls',
      'Dedicated onboarding and priority support',
      'Custom deployment, security, and compliance reviews',
    ],
  },
];

export const pricingPageStarterFeatures = [
  '1 AI Agent Deployment with OpenClaw deployment',
  '8GB RAM, 25 GB storage, shared VM',
  '$40/month of included AI credits',
  'Telegram Bot Integration',
  'Control UI Dashboard',
  'Web Browsing & Cron Jobs',
]

export const pricingPageProFeatures = [
  'Everything in Starter, plus:',
  '$100/month of included AI credits',
  'Brave Search API (5,000/mo) - $25 value',
  'Voice support included - Whisper STT + ElevenLabs TTS',
  'Everything pre-setup, paste your telegram token, pair bot & start',
  'Template Library (Lead Scout & more)',
  'Agent Onboarding Wizard · Usage Dashboard · Priority Support',
]

export const pricingPageEnterpriseFeatures =
  v2PricingPlans.find((plan) => plan.name === 'Enterprise')?.features ?? []

export const pricingPageIncludedDetails = [
  {
    icon: 'rocket',
    title: 'One-Click Deploy',
    description: 'Go from zero to a running AI agent in under 60 seconds. No servers to configure, no Docker knowledge needed.',
  },
  {
    icon: 'shield',
    title: 'Dedicated VM',
    description: 'Your agent runs on its own isolated virtual machine with guaranteed 24/7 uptime and automatic SSL.',
  },
  {
    icon: 'zap',
    title: 'Multi-Model Support',
    description: 'Swap between Claude, GPT-4o, Gemini, Llama, Mistral, DeepSeek, Venice, and more - any time.',
  },
  {
    icon: 'bot',
    title: 'Telegram Integration',
    description: 'Pair any Telegram bot in seconds with a unique pairing code. Your agent is instantly live on Telegram.',
  },
  {
    icon: 'monitor',
    title: 'Control Dashboard',
    description: 'Monitor real-time logs, adjust configuration, and manage your agent from a clean web UI.',
  },
  {
    icon: 'brain',
    title: 'Persistent Memory',
    description: 'Your agent remembers context across conversations so interactions feel natural and continuous.',
  },
  {
    icon: 'message-square',
    title: 'AI Support via Shelby',
    description: 'Get instant help from Shelby, our AI support agent, available 24/7 on Telegram.',
  },
  {
    icon: 'heart-pulse',
    title: 'Auto-Restart & Monitoring',
    description: 'Built-in health checks automatically restart your agent if anything goes wrong. Zero downtime.',
  },
]

export const pricingPageFaqItems = [
  {
    question: 'What\u2019s the difference between Starter and Pro?',
    answer:
      'Starter is our bring-your-own-key plan - you supply API keys from providers like Anthropic, OpenAI, or Google. Pro includes everything: bundled Kimi K2.6 credits, Brave Search API, voice support via Whisper STT and ElevenLabs TTS, ready-made templates, and an onboarding wizard so you can get productive immediately.',
  },
  {
    question: 'Is there a free trial?',
    answer:
      'We don\u2019t offer a free trial. Plans are month-to-month with no contracts - you can cancel anytime, and your agent keeps running until the end of your billing period.',
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards via our payment partner DodoPay.',
  },
  {
    question: 'Can I cancel anytime?',
    answer: 'Yes, cancel anytime. No long-term contracts or cancellation fees - ever.',
  },
  {
    question: 'What happens when I cancel?',
    answer:
      'Your agent continues running until the end of your billing period. After that, your deployment is gracefully stopped and your data is retained for 30 days in case you return.',
  },
  {
    question: 'Do I need my own API key?',
    answer:
      'On the Starter plan, yes - you bring your own API key from providers like Anthropic, OpenAI, Google, etc. On the Pro plan, a Kimi K2.6 model is included so you can start right away without any API keys.',
  },
  {
    question: 'Can I upgrade from Starter to Pro?',
    answer: 'Absolutely. You can upgrade at any time from your dashboard. You\u2019ll be prorated for the remainder of your billing period.',
  },
  {
    question: 'Is there a limit on usage?',
    answer:
      'No limit on messages or interactions. On Starter, you\u2019re only limited by your API provider\u2019s rate limits. On Pro, the included Kimi K2.6 model has generous built-in limits.',
  },
  {
    question: 'Do you offer team or enterprise plans?',
    answer: 'Yes. Enterprise plans are available for teams with volume, security, procurement, or custom deployment requirements. Contact us and we\u2019ll put together a tailored quote.',
  },
]
